// frontend/src/reducers/productReducers.js

import * as P from "../constants/productConstants";

/* -------------------------------------------------------------------------- */
/*                                   Facets                                   */
/* -------------------------------------------------------------------------- */

const createEmptyFacets = () => ({
  colors: [],
  materials: [],
  sizes: [],
  patterns: [],
  brands: [],
  unitTypes: [],
  finishes: [],
  compatibilities: [],
  genders: [],
  eras: [],
  tags: [],
  groups: [],
});

const normalizeFacetArray = (value) =>
  Array.isArray(value)
    ? [
        ...new Set(
          value.map((item) => String(item || "").trim()).filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b))
    : [];

const normalizeFacets = (facets = {}) => ({
  ...createEmptyFacets(),
  colors: normalizeFacetArray(facets.colors),
  materials: normalizeFacetArray(facets.materials),
  sizes: normalizeFacetArray(facets.sizes),
  patterns: normalizeFacetArray(facets.patterns),
  brands: normalizeFacetArray(facets.brands),
  unitTypes: normalizeFacetArray(facets.unitTypes),
  finishes: normalizeFacetArray(facets.finishes),
  compatibilities: normalizeFacetArray(facets.compatibilities),
  genders: normalizeFacetArray(facets.genders),
  eras: normalizeFacetArray(facets.eras),
  tags: normalizeFacetArray(facets.tags),
  groups: normalizeFacetArray(facets.groups),
});

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STOCK_STATUS = Object.freeze({
  AVAILABLE: "available",
  OUT_OF_STOCK: "out_of_stock",
  MAX_IN_CART: "max_in_cart",
  REQUESTED_QTY_EXCEEDS_AVAILABLE: "requested_qty_exceeds_available",
  INSUFFICIENT_STOCK: "insufficient_stock",
  SHARED_STOCK_EXHAUSTED_IN_CART: "shared_stock_exhausted_in_cart",
  SHARED_STOCK_BLOCKED_BY_CART: "shared_stock_blocked_by_cart",
  COMING_SOON: "coming_soon",
  UNAVAILABLE: "unavailable",
});

const PURCHASE_STATE = Object.freeze({
  ADDABLE: "ADDABLE",
  SOLD_OUT: "SOLD_OUT",
  IN_BAG: "IN_BAG",
  MAX_IN_CART: "MAX_IN_CART",
  INSUFFICIENT_STOCK: "INSUFFICIENT_STOCK",
  CHOOSE_ANOTHER_CUT: "CHOOSE_ANOTHER_CUT",
  COMING_SOON: "COMING_SOON",
  UNAVAILABLE: "UNAVAILABLE",
});

const ADD_BLOCKING_STOCK_STATUSES = new Set([
  STOCK_STATUS.OUT_OF_STOCK,
  "sold_out",
  STOCK_STATUS.MAX_IN_CART,
  STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE,
  STOCK_STATUS.INSUFFICIENT_STOCK,
  STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART,
  STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART,
  STOCK_STATUS.COMING_SOON,
  STOCK_STATUS.UNAVAILABLE,
  "reserved",
]);

const SELECT_BLOCKING_STOCK_STATUSES = new Set([
  STOCK_STATUS.OUT_OF_STOCK,
  "sold_out",
  STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART,
  STOCK_STATUS.COMING_SOON,
  STOCK_STATUS.UNAVAILABLE,
  "reserved",
]);

const BASKET_LIMITED_STOCK_STATUSES = new Set([
  STOCK_STATUS.MAX_IN_CART,
  STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART,
]);

const BLOCKING_PURCHASE_STATES = new Set([
  PURCHASE_STATE.SOLD_OUT,
  PURCHASE_STATE.MAX_IN_CART,
  PURCHASE_STATE.INSUFFICIENT_STOCK,
  PURCHASE_STATE.CHOOSE_ANOTHER_CUT,
  PURCHASE_STATE.COMING_SOON,
  PURCHASE_STATE.UNAVAILABLE,
]);

const SELECT_BLOCKING_PURCHASE_STATES = new Set([
  PURCHASE_STATE.SOLD_OUT,
  PURCHASE_STATE.CHOOSE_ANOTHER_CUT,
  PURCHASE_STATE.COMING_SOON,
  PURCHASE_STATE.UNAVAILABLE,
]);

const TEXTILE_SAMPLE_TYPES = new Set([
  "sample",
  "fabric_sample",
  "swatch",
  "cutting",
]);

const LENGTH_UNIT_TYPES = new Set([
  "length",
  "metre",
  "meter",
  "m",
  "half",
  "half_metre",
  "half_meter",
  "quarter",
  "quarter_metre",
  "quarter_meter",
  "yard",
  "half_yard",
  "quarter_yard",
]);

/* -------------------------------------------------------------------------- */
/*                              Error normalization                           */
/* -------------------------------------------------------------------------- */

function safeString(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpper(value = "") {
  return safeString(value).toUpperCase();
}

function normalizeLower(value = "") {
  return safeString(value).toLowerCase();
}

function toSafeStatus(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = normalizeLower(value);

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function customerSafeText(value = "", fallback = "") {
  const text = safeString(value);

  if (!text) return safeString(fallback);

  return containsUnsafeCustomerStockCopy(text) ? safeString(fallback) : text;
}

function normalizeErrorMeta(payload) {
  if (!payload) return null;

  if (typeof payload === "string") {
    return {
      message: customerSafeText(payload, "Something went wrong."),
      code: "",
      status: 0,
      requestId: "",
      issues: undefined,
    };
  }

  return {
    message: customerSafeText(payload?.message, "Something went wrong."),
    code: safeString(payload?.code),
    status: toSafeStatus(payload?.status, 0),
    requestId: safeString(payload?.requestId),
    issues: Array.isArray(payload?.issues) ? payload.issues : undefined,
  };
}

function normalizeErrorMessage(payload) {
  return normalizeErrorMeta(payload)?.message || null;
}

/* -------------------------------------------------------------------------- */
/*                              State factories                               */
/* -------------------------------------------------------------------------- */

const createEmptyProductsByShop = () => ({});

const createListState = () => ({
  loading: false,
  products: [],
  productsByShop: createEmptyProductsByShop(),
  facets: createEmptyFacets(),
  page: 1,
  pages: 1,
  count: 0,
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  },
  filters: {},
  error: null,
  errorMeta: null,
});

const createLatestState = () => ({
  loading: false,
  products: [],
  pagination: null,
  error: null,
});

const createFeaturedState = () => ({
  loading: false,
  products: [],
  pagination: null,
  error: null,
  errorMeta: null,
});

const createDetailsState = () => ({
  loading: false,
  product: null,
  error: null,
  errorMeta: null,
});

const createResolveState = () => ({
  loading: false,
  resolved: null,
  error: null,
  errorMeta: null,
  requestKey: "",
});

const createRecommendationsState = () => ({
  loading: false,
  products: [],
  count: 0,
  sourceProductId: "",
  error: null,
  errorMeta: null,
});

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function toNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function toNonNegativeNumber(value, fallback = 0) {
  return Math.max(0, toNumber(value, fallback));
}

function toNonNegativeInteger(value, fallback = 0) {
  const number = Math.floor(Number(value));

  if (!Number.isFinite(number) || number < 0) return fallback;

  return number;
}

function normalizeProductsByShop(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return createEmptyProductsByShop();
  }

  const out = {};

  for (const [shopKey, bucket] of Object.entries(value)) {
    if (Array.isArray(bucket)) {
      out[shopKey] = {
        simple: bucket.filter((product) => !product?.isVariant),
        variant: bucket.filter((product) => product?.isVariant),
        all: bucket,
      };
      continue;
    }

    const safeBucket = bucket && typeof bucket === "object" ? bucket : {};

    out[shopKey] = {
      simple: Array.isArray(safeBucket.simple) ? safeBucket.simple : [],
      variant: Array.isArray(safeBucket.variant) ? safeBucket.variant : [],
      all: Array.isArray(safeBucket.all) ? safeBucket.all : [],
    };
  }

  return out;
}

function normalizeStatusValue(value, fallback = "") {
  return normalizeLower(value || fallback);
}

function normalizePurchaseState(value = "") {
  const state = normalizeUpper(value);

  if (!state) return "";

  if (state === "OUT_OF_STOCK" || state === "SOLD_OUT") {
    return PURCHASE_STATE.SOLD_OUT;
  }

  if (state === "REQUESTED_QTY_EXCEEDS_AVAILABLE") {
    return PURCHASE_STATE.INSUFFICIENT_STOCK;
  }

  if (state === "INSUFFICIENT_STOCK") {
    return PURCHASE_STATE.INSUFFICIENT_STOCK;
  }

  if (state === "SHARED_STOCK_BLOCKED_BY_CART") {
    return PURCHASE_STATE.CHOOSE_ANOTHER_CUT;
  }

  if (state === "SHARED_STOCK_EXHAUSTED_IN_CART") {
    return PURCHASE_STATE.IN_BAG;
  }

  if (state === "MAX_IN_CART") {
    return PURCHASE_STATE.MAX_IN_CART;
  }

  if (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    state === "HELD" ||
    state === "ALLOCATED"
  ) {
    return PURCHASE_STATE.UNAVAILABLE;
  }

  if (Object.values(PURCHASE_STATE).includes(state)) return state;

  return "";
}

function normalizeRequestKey(value, fallback = "") {
  const next = safeString(value);

  return next || safeString(fallback);
}

function normalizePagination(payload = {}) {
  const pagination = payload?.pagination || {};

  const page = Math.max(1, Number(pagination.page || payload.page || 1));
  const limit = Math.max(1, Number(pagination.limit || payload.limit || 12));

  const total = Math.max(
    0,
    Number(pagination.total ?? payload.total ?? payload.count ?? 0),
  );

  const totalPages = Math.max(
    1,
    Number(pagination.totalPages || payload.totalPages || payload.pages || 1),
  );

  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage:
      typeof pagination.hasNextPage === "boolean"
        ? pagination.hasNextPage
        : page < totalPages,
    hasPrevPage:
      typeof pagination.hasPrevPage === "boolean"
        ? pagination.hasPrevPage
        : page > 1,
  };
}

function optionLooksLikeSample(option = {}) {
  return TEXTILE_SAMPLE_TYPES.has(normalizeLower(option?.variantType));
}

function optionLooksLikeLength(option = {}) {
  const variantType = normalizeLower(option?.variantType);
  const unitLabel = normalizeLower(option?.unitLabel);
  const itemType = normalizeLower(option?.itemType);
  const productType = normalizeLower(option?.productType);

  if (optionLooksLikeSample(option)) return false;

  return Boolean(
    LENGTH_UNIT_TYPES.has(variantType) ||
    unitLabel.includes("metre") ||
    unitLabel.includes("meter") ||
    unitLabel === "m" ||
    unitLabel.includes("yard") ||
    ((itemType === "textile" || productType === "textile") &&
      LENGTH_UNIT_TYPES.has(variantType)),
  );
}

function getUnitValue(option = {}) {
  const value = Number(option?.unitValue ?? 1);

  if (!Number.isFinite(value) || value <= 0) return 1;

  return value;
}

function getAvailableStockAmount(option = {}) {
  return toNonNegativeNumber(
    option?.available ??
      option?.catalogAvailable ??
      option?.stockAvailable ??
      0,
    0,
  );
}

function getMaxPurchasableQty(option = {}) {
  const explicitMax =
    option?.maxAddableQty ??
    option?.remainingAddableQty ??
    option?.maxPurchasableQty;

  if (explicitMax !== undefined && explicitMax !== null) {
    return toNonNegativeInteger(explicitMax, 0);
  }

  const available = getAvailableStockAmount(option);

  if (available <= 0) return 0;

  if (optionLooksLikeLength(option)) {
    return Math.max(0, Math.floor(available / getUnitValue(option)));
  }

  return Math.max(0, Math.floor(available));
}

function getRequiredStockAmount(option = {}, qty = 1) {
  const safeQty = Math.max(1, toNonNegativeInteger(qty, 1));

  if (optionLooksLikeLength(option)) {
    return Math.round(getUnitValue(option) * safeQty * 1000) / 1000;
  }

  return safeQty;
}

function fallbackMessageForOption({
  status = "",
  purchaseState = "",
  maxAddableQty = 0,
  available = 0,
} = {}) {
  const safeStatus = normalizeStatusValue(status);
  const safePurchaseState = normalizePurchaseState(purchaseState);

  if (
    safePurchaseState === PURCHASE_STATE.MAX_IN_CART ||
    safeStatus === STOCK_STATUS.MAX_IN_CART
  ) {
    return "This option is already in your bag.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.IN_BAG ||
    safeStatus === STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART
  ) {
    return "This cut is already in your bag.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.CHOOSE_ANOTHER_CUT ||
    safeStatus === STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART
  ) {
    return "Choose another cut from this shared roll.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.INSUFFICIENT_STOCK ||
    safeStatus === STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE ||
    safeStatus === STOCK_STATUS.INSUFFICIENT_STOCK
  ) {
    if (maxAddableQty === 1) return "Only 1 more can be added to your bag.";

    if (maxAddableQty > 1) {
      return `Only ${maxAddableQty} more can be added to your bag.`;
    }

    return "The selected quantity is no longer available.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.SOLD_OUT ||
    safeStatus === STOCK_STATUS.OUT_OF_STOCK ||
    safeStatus === "sold_out"
  ) {
    return "Sold out.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.COMING_SOON ||
    safeStatus === STOCK_STATUS.COMING_SOON
  ) {
    return "Coming soon.";
  }

  if (
    safePurchaseState === PURCHASE_STATE.UNAVAILABLE ||
    safeStatus === STOCK_STATUS.UNAVAILABLE ||
    safeStatus === "reserved"
  ) {
    return "Currently unavailable.";
  }

  const qty = Math.floor(Number(maxAddableQty || available || 0));

  if (qty <= 0) return "Unavailable.";
  if (qty === 1) return "Only 1 available";
  if (qty <= 3) return `Only ${qty} available`;

  return "Available";
}

function getAvailabilityMessage({
  status,
  purchaseState,
  available,
  maxAddableQty,
  option,
} = {}) {
  const fallback = fallbackMessageForOption({
    status,
    purchaseState,
    available,
    maxAddableQty,
  });

  return customerSafeText(
    option?.purchaseMessage ||
      option?.availableLabel ||
      option?.statusReason ||
      option?.stockStatusReason ||
      option?.disabledReason ||
      option?.unavailableReason,
    fallback,
  );
}

function deriveStatus({ option = {}, qty = 1, explicitCanPurchase } = {}) {
  const explicitStatus = normalizeStatusValue(
    option?.status || option?.stockStatus,
  );

  if (explicitStatus) {
    if (explicitStatus === "reserved") return STOCK_STATUS.UNAVAILABLE;
    return explicitStatus;
  }

  const purchaseState = normalizePurchaseState(
    option?.purchaseState || option?.unavailableState,
  );

  if (purchaseState === PURCHASE_STATE.MAX_IN_CART) {
    return STOCK_STATUS.MAX_IN_CART;
  }

  if (purchaseState === PURCHASE_STATE.IN_BAG) {
    return STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART;
  }

  if (purchaseState === PURCHASE_STATE.CHOOSE_ANOTHER_CUT) {
    return STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART;
  }

  if (purchaseState === PURCHASE_STATE.SOLD_OUT) {
    return STOCK_STATUS.OUT_OF_STOCK;
  }

  if (purchaseState === PURCHASE_STATE.COMING_SOON) {
    return STOCK_STATUS.COMING_SOON;
  }

  if (purchaseState === PURCHASE_STATE.INSUFFICIENT_STOCK) {
    return STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE;
  }

  if (purchaseState === PURCHASE_STATE.UNAVAILABLE) {
    return STOCK_STATUS.UNAVAILABLE;
  }

  const stockStatusCode = normalizeUpper(
    option?.stockStatusCode || option?.statusCode || option?.unavailableCode,
  );

  if (stockStatusCode === "MAX_IN_CART") return STOCK_STATUS.MAX_IN_CART;

  if (stockStatusCode === "SHARED_STOCK_EXHAUSTED_IN_CART") {
    return STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART;
  }

  if (
    stockStatusCode === "SHARED_STOCK_BLOCKED_BY_CART" ||
    stockStatusCode === "INSUFFICIENT_SHARED_STOCK"
  ) {
    return STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART;
  }

  if (
    stockStatusCode === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    stockStatusCode === "INSUFFICIENT_STOCK"
  ) {
    return STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE;
  }

  if (stockStatusCode === "OUT_OF_STOCK") return STOCK_STATUS.OUT_OF_STOCK;
  if (stockStatusCode === "UNAVAILABLE") return STOCK_STATUS.UNAVAILABLE;

  const available = getAvailableStockAmount(option);

  if (available <= 0) return STOCK_STATUS.OUT_OF_STOCK;

  if (typeof explicitCanPurchase === "boolean") {
    return explicitCanPurchase
      ? STOCK_STATUS.AVAILABLE
      : STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE;
  }

  const required = getRequiredStockAmount(option, qty);

  return available >= required
    ? STOCK_STATUS.AVAILABLE
    : STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE;
}

function derivePurchaseState({ option = {}, status, canAddToCart }) {
  const explicitPurchaseState = normalizePurchaseState(
    option?.purchaseState || option?.unavailableState,
  );

  if (explicitPurchaseState) return explicitPurchaseState;

  if (status === STOCK_STATUS.AVAILABLE && canAddToCart) {
    return PURCHASE_STATE.ADDABLE;
  }

  if (status === STOCK_STATUS.MAX_IN_CART) {
    return PURCHASE_STATE.MAX_IN_CART;
  }

  if (status === STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART) {
    return PURCHASE_STATE.IN_BAG;
  }

  if (status === STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART) {
    return PURCHASE_STATE.CHOOSE_ANOTHER_CUT;
  }

  if (status === STOCK_STATUS.OUT_OF_STOCK || status === "sold_out") {
    return PURCHASE_STATE.SOLD_OUT;
  }

  if (status === STOCK_STATUS.COMING_SOON) {
    return PURCHASE_STATE.COMING_SOON;
  }

  if (
    status === STOCK_STATUS.REQUESTED_QTY_EXCEEDS_AVAILABLE ||
    status === STOCK_STATUS.INSUFFICIENT_STOCK
  ) {
    return PURCHASE_STATE.INSUFFICIENT_STOCK;
  }

  return canAddToCart ? PURCHASE_STATE.ADDABLE : PURCHASE_STATE.UNAVAILABLE;
}

function normalizeResolveOption(
  option = {},
  {
    qty = 1,
    product = null,
    explicitCanPurchase,
    selected = false,
    request = null,
  } = {},
) {
  const safeQty = Math.max(1, toNonNegativeInteger(qty, 1));
  const available = getAvailableStockAmount(option);
  const maxPurchasableQty = getMaxPurchasableQty(option);

  const status = deriveStatus({
    option,
    qty: safeQty,
    explicitCanPurchase,
  });

  const addBlockedByStatus = ADD_BLOCKING_STOCK_STATUSES.has(status);
  const selectBlockedByStatus = SELECT_BLOCKING_STOCK_STATUSES.has(status);

  const rawCanAddToCart = hasOwn(option, "canAddToCart")
    ? Boolean(option?.canAddToCart)
    : hasOwn(option, "isAddable")
      ? Boolean(option?.isAddable)
      : maxPurchasableQty > 0;

  const canAddToCart = rawCanAddToCart && !addBlockedByStatus;

  const purchaseState = derivePurchaseState({
    option,
    status,
    canAddToCart,
  });

  const purchaseStateAddBlocked = BLOCKING_PURCHASE_STATES.has(purchaseState);
  const purchaseStateSelectBlocked =
    SELECT_BLOCKING_PURCHASE_STATES.has(purchaseState);

  const finalCanAddToCart = canAddToCart && !purchaseStateAddBlocked;

  const finalCanSelect =
    option?.disabled === true
      ? false
      : !selectBlockedByStatus && !purchaseStateSelectBlocked;

  const variantSku = normalizeUpper(
    option?.variantSku ?? option?.value?.variantSku,
  );

  const variantType = normalizeLower(
    option?.variantType ?? option?.value?.variantType,
  );

  const alreadyInCartQty = toNonNegativeInteger(
    option?.alreadyInCartQty ?? option?.qtyInCart ?? 0,
    0,
  );

  const remainingAddableQty = hasOwn(option, "remainingAddableQty")
    ? toNonNegativeInteger(option?.remainingAddableQty, 0)
    : maxPurchasableQty;

  const maxAddableQty = hasOwn(option, "maxAddableQty")
    ? toNonNegativeInteger(option?.maxAddableQty, 0)
    : remainingAddableQty;

  const requiredStockAmount = getRequiredStockAmount(option, safeQty);

  const purchaseMessage = getAvailabilityMessage({
    status,
    purchaseState,
    available,
    maxAddableQty,
    option,
  });

  const displayable = option?.isDisplayable !== false;
  const displayInOptions = option?.displayInOptions !== false;

  return {
    ...option,

    selectionKey: safeString(option?.selectionKey),
    selectionLabel: safeString(
      option?.selectionLabel ||
        option?.label ||
        option?.variantName ||
        option?.name ||
        option?.size ||
        option?.length,
    ),

    productId: safeString(option?.productId || product?._id || product?.id),
    productType: normalizeLower(option?.productType || product?.productType),
    itemType: normalizeLower(option?.itemType),

    sku: normalizeUpper(option?.sku),
    variantSku,
    variantType,
    variantName: safeString(option?.variantName || option?.name),

    image: safeString(option?.image),
    images: Array.isArray(option?.images) ? option.images : [],

    unitLabel: safeString(option?.unitLabel),
    unitPrice: Number(option?.unitPrice ?? option?.price ?? 0),
    price: Number(option?.price ?? option?.unitPrice ?? 0),
    unitValue: getUnitValue(option),
    itemsPerPack: Math.max(1, toNonNegativeInteger(option?.itemsPerPack, 1)),

    available,
    catalogAvailable: toNonNegativeNumber(
      option?.catalogAvailable ?? available,
      0,
    ),
    requiredStockAmount,

    alreadyInCartQty,
    qtyInCart: alreadyInCartQty,
    alreadyInCartStockAmount: toNonNegativeNumber(
      option?.alreadyInCartStockAmount,
      0,
    ),

    remainingStockAmount: toNonNegativeNumber(option?.remainingStockAmount, 0),
    remainingAddableQty,
    maxAddableQty,
    maxPurchasableQty,

    sharedRollStockMetres: toNonNegativeNumber(
      option?.sharedRollStockMetres,
      0,
    ),

    canAddToCart: finalCanAddToCart,
    canPurchase: hasOwn(option, "canPurchase")
      ? Boolean(option?.canPurchase) && finalCanAddToCart
      : finalCanAddToCart,

    canIncreaseQty: hasOwn(option, "canIncreaseQty")
      ? Boolean(option?.canIncreaseQty) && finalCanAddToCart
      : finalCanAddToCart && maxAddableQty > safeQty,

    isDisplayable: displayable,
    displayInOptions,

    isAddable: hasOwn(option, "isAddable")
      ? Boolean(option?.isAddable) && finalCanAddToCart
      : finalCanAddToCart,

    isAvailable: hasOwn(option, "isAvailable")
      ? Boolean(option?.isAvailable) && finalCanSelect
      : finalCanSelect,

    disabled: hasOwn(option, "disabled")
      ? Boolean(option?.disabled) || !finalCanSelect
      : !finalCanSelect,

    canSelect: hasOwn(option, "canSelect")
      ? Boolean(option?.canSelect) && finalCanSelect
      : finalCanSelect,

    isSelectable: hasOwn(option, "isSelectable")
      ? Boolean(option?.isSelectable) && finalCanSelect
      : finalCanSelect,

    sharedPoolBlockedByCart: Boolean(option?.sharedPoolBlockedByCart),
    sharedPoolReducedByCart: Boolean(option?.sharedPoolReducedByCart),

    isSelected: Boolean(selected || option?.isSelected),

    purchaseState,

    unavailableState:
      finalCanAddToCart && purchaseState === PURCHASE_STATE.ADDABLE
        ? ""
        : purchaseState,

    purchaseMessage,
    availableLabel: customerSafeText(option?.availableLabel, purchaseMessage),

    status,
    stockStatus: status,
    statusCode: safeString(
      option?.statusCode || option?.stockStatusCode || status,
    ),
    stockStatusCode: safeString(
      option?.stockStatusCode || option?.statusCode || status,
    ),

    statusReason: customerSafeText(
      option?.statusReason ||
        option?.stockStatusReason ||
        option?.disabledReason ||
        option?.unavailableReason,
      purchaseMessage,
    ),

    stockStatusReason: customerSafeText(
      option?.stockStatusReason ||
        option?.statusReason ||
        option?.disabledReason ||
        option?.unavailableReason,
      purchaseMessage,
    ),

    disabledReason: finalCanAddToCart
      ? ""
      : customerSafeText(option?.disabledReason, purchaseMessage),

    unavailableReason: finalCanAddToCart
      ? ""
      : customerSafeText(option?.unavailableReason, purchaseMessage),

    color: safeString(option?.color || option?.colour),
    colour: safeString(option?.colour || option?.color),
    size: safeString(option?.size),
    material: safeString(option?.material),
    length: safeString(option?.length),

    isInBag: BASKET_LIMITED_STOCK_STATUSES.has(status),
    isMaxedInCart: status === STOCK_STATUS.MAX_IN_CART,
    isSharedStockInBag: status === STOCK_STATUS.SHARED_STOCK_EXHAUSTED_IN_CART,
    isSharedStockBlocked: status === STOCK_STATUS.SHARED_STOCK_BLOCKED_BY_CART,

    request: request
      ? {
          ...request,
          qty: safeQty,
          variantSku: normalizeUpper(request?.variantSku),
          variantType: normalizeLower(request?.variantType),
        }
      : undefined,
  };
}

function normalizeBackendPurchasePayload(payload = {}) {
  const product = payload?.product || null;
  const qty = Math.max(1, toNonNegativeInteger(payload?.qty, 1));

  const request = {
    ...(payload?.request || {}),
    ...(payload?.selection || {}),
    qty,
    variantSku: normalizeUpper(
      payload?.request?.variantSku || payload?.selection?.variantSku,
    ),
    variantType: normalizeLower(
      payload?.request?.variantType || payload?.selection?.variantType,
    ),
  };

  const selectedSource =
    payload?.selected ||
    payload?.resolved ||
    payload?.purchase ||
    payload?.resolvedPurchase ||
    null;

  const selected = selectedSource
    ? normalizeResolveOption(selectedSource, {
        qty,
        product,
        explicitCanPurchase:
          hasOwn(payload, "canPurchase") ||
          hasOwn(selectedSource, "canPurchase")
            ? Boolean(payload?.canPurchase ?? selectedSource?.canPurchase)
            : undefined,
        selected: true,
        request,
      })
    : null;

  const optionSources = Array.isArray(payload?.options)
    ? payload.options
    : Array.isArray(payload?.purchasableOptions)
      ? payload.purchasableOptions
      : [];

  const normalizedOptions = optionSources.map((option) => {
    const matchesSelected = Boolean(
      selected &&
      normalizeUpper(option?.variantSku ?? option?.value?.variantSku) ===
        selected.variantSku &&
      normalizeLower(option?.variantType ?? option?.value?.variantType) ===
        selected.variantType,
    );

    return normalizeResolveOption(option, {
      qty,
      product,
      selected: matchesSelected || Boolean(option?.isSelected),
      request,
    });
  });

  const options = normalizedOptions.length
    ? normalizedOptions
    : selected
      ? [selected]
      : [];

  return {
    ...payload,

    qty,
    currency: safeString(payload?.currency || product?.currency || "GBP"),

    product,

    request,
    requested: selected,
    selected,

    options,

    defaultOption:
      payload?.defaultOption || payload?.defaultPurchasableOption || null,

    defaultPurchasableOption:
      payload?.defaultPurchasableOption || payload?.defaultOption || null,

    purchase: selected || payload?.resolved || payload?.purchase || null,
    resolvedPurchase:
      selected || payload?.resolvedPurchase || payload?.resolved || null,
    resolved: selected || payload?.resolved || null,

    available: toNonNegativeNumber(
      payload?.available ?? selected?.available ?? product?.available ?? 0,
      0,
    ),

    requiredStockAmount: toNonNegativeNumber(
      payload?.requiredStockAmount ?? selected?.requiredStockAmount ?? 0,
      0,
    ),

    alreadyInCartQty: toNonNegativeInteger(
      payload?.alreadyInCartQty ?? selected?.alreadyInCartQty ?? 0,
      0,
    ),

    qtyInCart: toNonNegativeInteger(
      payload?.qtyInCart ??
        payload?.alreadyInCartQty ??
        selected?.qtyInCart ??
        0,
      0,
    ),

    alreadyInCartStockAmount: toNonNegativeNumber(
      payload?.alreadyInCartStockAmount ??
        selected?.alreadyInCartStockAmount ??
        0,
      0,
    ),

    remainingStockAmount: toNonNegativeNumber(
      payload?.remainingStockAmount ?? selected?.remainingStockAmount ?? 0,
      0,
    ),

    remainingAddableQty: toNonNegativeInteger(
      payload?.remainingAddableQty ?? selected?.remainingAddableQty ?? 0,
      0,
    ),

    maxAddableQty: toNonNegativeInteger(
      payload?.maxAddableQty ?? selected?.maxAddableQty ?? 0,
      0,
    ),

    canPurchase: hasOwn(payload, "canPurchase")
      ? Boolean(payload.canPurchase) && Boolean(selected?.canPurchase)
      : Boolean(selected?.canPurchase || selected?.canAddToCart),

    canAddToCart: hasOwn(payload, "canAddToCart")
      ? Boolean(payload.canAddToCart) && Boolean(selected?.canAddToCart)
      : Boolean(selected?.canAddToCart),

    canIncreaseQty: hasOwn(payload, "canIncreaseQty")
      ? Boolean(payload.canIncreaseQty) && Boolean(selected?.canIncreaseQty)
      : Boolean(selected?.canIncreaseQty),

    isDisplayable: hasOwn(payload, "isDisplayable")
      ? Boolean(payload.isDisplayable)
      : selected?.isDisplayable !== false,

    isAddable: hasOwn(payload, "isAddable")
      ? Boolean(payload.isAddable) && Boolean(selected?.isAddable)
      : Boolean(selected?.isAddable),

    isAvailable: hasOwn(payload, "isAvailable")
      ? Boolean(payload.isAvailable) && Boolean(selected?.isAvailable)
      : Boolean(selected?.isAvailable),

    disabled: hasOwn(payload, "disabled")
      ? Boolean(payload.disabled) || Boolean(selected?.disabled)
      : Boolean(selected?.disabled),

    canSelect: Boolean(selected?.canSelect || selected?.isSelectable),
    isSelectable: Boolean(selected?.isSelectable || selected?.canSelect),

    purchaseState:
      normalizePurchaseState(payload?.purchaseState) ||
      selected?.purchaseState ||
      "",

    unavailableState:
      normalizePurchaseState(payload?.unavailableState) ||
      selected?.unavailableState ||
      "",

    status: normalizeStatusValue(payload?.status || selected?.status),
    stockStatus: normalizeStatusValue(
      payload?.stockStatus || selected?.stockStatus,
    ),

    statusCode: safeString(payload?.statusCode || selected?.statusCode),

    stockStatusCode: safeString(
      payload?.stockStatusCode || selected?.stockStatusCode,
    ),

    purchaseMessage: customerSafeText(
      payload?.purchaseMessage,
      selected?.purchaseMessage,
    ),

    availableLabel: customerSafeText(
      payload?.availableLabel,
      selected?.availableLabel,
    ),

    statusReason: customerSafeText(
      payload?.statusReason,
      selected?.statusReason,
    ),

    stockStatusReason: customerSafeText(
      payload?.stockStatusReason,
      selected?.stockStatusReason,
    ),

    disabledReason: customerSafeText(
      payload?.disabledReason,
      selected?.disabledReason,
    ),

    unavailableReason: customerSafeText(
      payload?.unavailableReason,
      selected?.unavailableReason,
    ),
  };
}

function normalizeResolvePayload(payload = {}) {
  if (!payload || typeof payload !== "object") {
    return normalizeBackendPurchasePayload({});
  }

  return normalizeBackendPurchasePayload(payload);
}

function buildListSuccessState(payload = {}) {
  const pagination = normalizePagination(payload);

  return {
    loading: false,
    products: Array.isArray(payload?.products) ? payload.products : [],
    productsByShop: normalizeProductsByShop(payload?.productsByShop),
    facets: normalizeFacets(payload?.facets || {}),
    page: pagination.page,
    pages: pagination.totalPages,
    count: pagination.total,
    pagination,
    filters:
      payload?.filters && typeof payload.filters === "object"
        ? payload.filters
        : {},
    error: null,
    errorMeta: null,
  };
}

function normalizeRecommendationsPayload(payload = {}) {
  const products = Array.isArray(payload?.products) ? payload.products : [];

  return {
    loading: false,
    products,
    count: Number(payload?.count ?? products.length) || products.length,
    sourceProductId: safeString(payload?.sourceProductId),
    error: null,
    errorMeta: null,
  };
}

/* -------------------------------------------------------------------------- */
/*                                    Public                                  */
/* -------------------------------------------------------------------------- */

export const productListReducer = (state = createListState(), action) => {
  switch (action.type) {
    case P.PRODUCT_LIST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_LIST_SUCCESS:
      return buildListSuccessState(action.payload);

    case P.PRODUCT_LIST_FAIL:
      return {
        ...state,
        loading: false,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case P.PRODUCT_LIST_RESET:
      return createListState();

    default:
      return state;
  }
};

export const productLatestReducer = (state = createLatestState(), action) => {
  switch (action.type) {
    case P.PRODUCT_LATEST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case P.PRODUCT_LATEST_SUCCESS:
      return {
        loading: false,
        products: Array.isArray(action.payload?.products)
          ? action.payload.products
          : [],
        pagination: action.payload?.pagination || null,
        error: null,
      };

    case P.PRODUCT_LATEST_FAIL:
      return {
        ...state,
        loading: false,
        error: normalizeErrorMessage(action.payload),
      };

    case P.PRODUCT_LATEST_RESET:
      return createLatestState();

    default:
      return state;
  }
};

export const productFeaturedReducer = (
  state = createFeaturedState(),
  action,
) => {
  switch (action.type) {
    case P.PRODUCT_FEATURED_REQUEST:
      return {
        ...state,
        loading: true,
        products: [],
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_FEATURED_SUCCESS:
      return {
        loading: false,
        products: Array.isArray(action.payload?.products)
          ? action.payload.products
          : [],
        pagination: action.payload?.pagination || null,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_FEATURED_FAIL:
      return {
        ...state,
        loading: false,
        products: [],
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case P.PRODUCT_FEATURED_RESET:
      return createFeaturedState();

    default:
      return state;
  }
};

export const productDetailsReducer = (state = createDetailsState(), action) => {
  switch (action.type) {
    case P.PRODUCT_DETAILS_REQUEST:
      return {
        loading: true,
        product: null,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_DETAILS_SUCCESS:
      return {
        loading: false,
        product: action.payload,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_DETAILS_FAIL:
      return {
        loading: false,
        product: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case P.PRODUCT_DETAILS_RESET:
      return createDetailsState();

    default:
      return state;
  }
};

export const productBySlugReducer = (state = createDetailsState(), action) => {
  switch (action.type) {
    case P.PRODUCT_BY_SLUG_REQUEST:
      return {
        loading: true,
        product: null,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_BY_SLUG_SUCCESS:
      return {
        loading: false,
        product: action.payload,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_BY_SLUG_FAIL:
      return {
        loading: false,
        product: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case P.PRODUCT_BY_SLUG_RESET:
      return createDetailsState();

    default:
      return state;
  }
};

export const productResolveReducer = (state = createResolveState(), action) => {
  switch (action.type) {
    case P.PRODUCT_RESOLVE_REQUEST: {
      const requestKey = normalizeRequestKey(
        action.meta?.requestKey,
        state.requestKey,
      );

      const isNewRequest = Boolean(
        requestKey && requestKey !== state.requestKey,
      );

      return {
        ...state,
        loading: true,
        error: null,
        errorMeta: null,
        requestKey,
        resolved: isNewRequest ? null : state.resolved,
      };
    }

    case P.PRODUCT_RESOLVE_SUCCESS: {
      const incomingKey = normalizeRequestKey(action.meta?.requestKey);

      if (incomingKey && state.requestKey && incomingKey !== state.requestKey) {
        return state;
      }

      return {
        ...state,
        loading: false,
        resolved: normalizeResolvePayload(action.payload),
        error: null,
        errorMeta: null,
      };
    }

    case P.PRODUCT_RESOLVE_FAIL: {
      const incomingKey = normalizeRequestKey(action.meta?.requestKey);

      if (incomingKey && state.requestKey && incomingKey !== state.requestKey) {
        return state;
      }

      return {
        ...state,
        loading: false,
        resolved: null,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };
    }

    case P.PRODUCT_RESOLVE_RESET:
      return createResolveState();

    default:
      return state;
  }
};

export const productRecommendationsReducer = (
  state = createRecommendationsState(),
  action,
) => {
  switch (action.type) {
    case P.PRODUCT_RECOMMENDATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        errorMeta: null,
      };

    case P.PRODUCT_RECOMMENDATIONS_SUCCESS:
      return normalizeRecommendationsPayload(action.payload);

    case P.PRODUCT_RECOMMENDATIONS_FAIL:
      return {
        ...state,
        loading: false,
        error: normalizeErrorMessage(action.payload),
        errorMeta: normalizeErrorMeta(action.payload),
      };

    case P.PRODUCT_RECOMMENDATIONS_RESET:
      return createRecommendationsState();

    default:
      return state;
  }
};
